import React, { useState, useEffect, useMemo } from 'react'
import { Card, Col, Row } from 'react-bootstrap'
import Breadcrumbs from '../../../shared/components/Breadcrumbs'
import DefaultButton from '../../../shared/components/DefaultButton'
import { REGION, CREATENEW, IMPORTREGION, IMPORT, CREATEREGION } from '../../../shared/constants/Constants'
import ServerSideGrid from '../../../shared/components/listview/ServerSideGrid'
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom'
import { loadRegionDataRequest, loadRegionImportDataRequest } from '../redux/Actions'
import Modalpopup from '../../../shared/components/Modalpopup'
import RegionAdd from './RegionAdd'
import { loadCompanyUserGetDataRequest } from '../../company/redux/Actions'
import ImportModal from '../../../shared/components/ImportModal'
import Permissions from '../../../shared/components/Permissions'

const Region = () => {
    let dispatch = useDispatch();
    let navigate = useNavigate();
    const [show, setShow] = useState(false);
    let { loading, error, regions, isImportFile } = useSelector((state) => state.region);
    let { userCompany } = useSelector((state) => state.company);

    // Load the user's preferred company on mount
    useEffect(() => {
        dispatch(loadCompanyUserGetDataRequest())
    }, [dispatch])

    // Once we know the user's preferred company (or lack of one), load regions accordingly
    useEffect(() => {
        const companyIds = userCompany?.data?.company_id;
        if (Array.isArray(companyIds) && companyIds.length > 0) {
            dispatch(loadRegionDataRequest(companyIds));
        } else {
            dispatch(loadRegionDataRequest());
        }
    }, [dispatch, userCompany])

    const handleClose = () => {
        setShow(false);
    };
    const handleShow = () => {
        setShow(true);
    };

    // Only show active regions on first load — grid's own filter (Status column) drives this,
    // so the user can change it afterward without us re-slicing the data.
    const defaultActiveFilter = useMemo(() => ({
        filterModel: {
            active: { filterType: 'set', values: ['Active'] },
        },
    }), []);

    // Maps the numeric "active" code to a display label, used for both rendering and the set filter
    const STATUS_LABELS = { 0: 'Active', 1: 'Inactive', 2: 'Upcoming' };

    // Status badge renderer for the "active" code (0 = Active, 1 = Inactive, 2 = Upcoming)
    const statusCellRenderer = (params) => {
        const val = params.value;
        const config = {
            0: { label: 'Active', color: '#198754', bg: 'rgba(25,135,84,0.15)' },
            1: { label: 'Inactive', color: '#dc3545', bg: 'rgba(220,53,69,0.15)' },
            2: { label: 'Upcoming', color: '#0d6efd', bg: 'rgba(13,110,253,0.15)' },
        };
        const { label, color, bg } = config[val] || { label: '-', color: '#666', bg: 'transparent' };
        return (
            <span
                style={{
                    padding: '3px 12px',
                    borderRadius: '8px',
                    fontWeight: 600,
                    color,
                    backgroundColor: bg,
                    fontSize: '13px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '70px',
                    height: '24px',
                }}
            >
                {label}
            </span>
        );
    };

    // Date columns come back as 'mm-dd-yyyy' strings — parse for correct filter comparison
    const dateFilterParams = useMemo(() => ({
        comparator: (filterLocalDateAtMidnight, cellValue) => {
            if (!cellValue) return -1;
            const [month, day, year] = cellValue.split('-').map(Number);
            const cellDate = new Date(year, month - 1, day);
            if (cellDate < filterLocalDateAtMidnight) return -1;
            if (cellDate > filterLocalDateAtMidnight) return 1;
            return 0;
        },
    }), []);

    const columnDefs = useMemo(() => [
        {
            headerName: 'Region Name',
            field: 'region_name',
            sortable: true,
            flex: 1,
            minWidth: 180,
            filter: 'agTextColumnFilter',
        },
        {
            headerName: 'Ulti Region Code',
            field: 'ulti_region_code',
            sortable: true,
            width: 180,
            filter: 'agTextColumnFilter',
        },
        {
            headerName: 'Directors',
            field: 'manager',
            sortable: true,
            width: 200,
            filter: 'agTextColumnFilter',
        },
        {
            headerName: 'Division Name',
            field: 'division_name',
            sortable: true,
            width: 180,
            filter: 'agTextColumnFilter',
        },
        {
            headerName: 'Start Date',
            field: 'start_date',
            sortable: true,
            width: 140,
            filter: 'agDateColumnFilter',
            filterParams: dateFilterParams,
        },
        {
            headerName: 'End Date',
            field: 'end_date',
            sortable: true,
            width: 140,
            filter: 'agDateColumnFilter',
            filterParams: dateFilterParams,
        },
        {
            headerName: 'Status',
            field: 'active',
            sortable: true,
            width: 130,
            filter: 'agSetColumnFilter',
            filterValueGetter: (params) => STATUS_LABELS[params.data.active] ?? params.data.active,
            filterParams: { values: ['Active', 'Inactive'] },
            cellRenderer: statusCellRenderer,
        },
    ], [dateFilterParams]);

    const [showImportModal, setShowImportModal] = useState(false);

    let emp_no = JSON.parse(localStorage.getItem("emp_no"));
    let module_name = "Region";
    let permission = Permissions(emp_no, module_name);

    return (
        <>
            <Row>
                <Col className="gx-0">
                    <Breadcrumbs
                        children={[
                            { href: '/region', label: REGION },
                        ]}
                    />
                </Col>
                <Col xs="auto" className="gx-0">
                    {permission.create_permission ? (
                        <>
                            <DefaultButton
                                buttonClass={"default-btn-color me-1"}
                                buttonTitle={IMPORT}
                                actionBlock={() => { setShowImportModal(true) }}
                            />
                            <DefaultButton buttonClass={'default-btn-color'} buttonTitle={CREATENEW} actionBlock={handleShow}></DefaultButton>
                        </>
                    ) : (<></>)}
                </Col>
            </Row>
            <Row>
                <Card className='table-card'>
                    <Card.Body>
                        <ServerSideGrid
                            clientSide={true}
                            rowData={regions || []}
                            columnDefs={columnDefs}
                            externalFilter={defaultActiveFilter}
                            tableHeight='70vh'
                            showPagination={true}
                            paginationPageSize={80}
                            theme="alpine"
                            showExport
                            showExportAll
                            showClearFilter
                            exportFileName={`${REGION}_${new Date().toISOString().slice(0, 10)}`}
                            onRowClicked={(row) => { navigate('./' + row.data['id']) }}
                        />
                    </Card.Body>
                </Card>
            </Row>
            <Modalpopup
                show={show}
                handleClose={() => handleClose()}
                actionTitle={CREATEREGION}
            >
                <RegionAdd handleClose={(reload) => {
                    setShow(false);
                    if (reload) {
                        dispatch(loadRegionDataRequest(userCompany && userCompany.id ? userCompany.id : undefined));
                    }
                }} />
            </Modalpopup>
            <ImportModal
                show={showImportModal}
                handleClose={() => setShowImportModal(false)}
                tableName={"region"}
                modalTitle={IMPORTREGION}
                importAction={loadRegionImportDataRequest}
            />
        </>
    )
}

export default Region